/**
 * hooks/useCardLayout.js — fetch and mutate the tenant's custom card
 * layout.
 *
 * Chunk 3 (Apr 24 2026).
 *
 * IMPORTANT for Stan: this imports `apiFetch` and `ApiError` from
 * `../lib/api.js`. If the existing hooks use a different helper name
 * (e.g. `api.get` / `api.put`), update the two call sites below to
 * match. The behaviour needed is: "async fetch that auto-attaches the
 * session header, parses JSON, and throws ApiError on non-2xx".
 * Same thing useCardSettings.js already does.
 *
 * API:
 *   const { layout, loading, error, refresh, saveLayout, resetLayout }
 *     = useCardLayout();
 *
 *   layout     — parsed layout object, or null if no custom layout saved
 *   loading    — initial fetch in flight
 *   error      — fetch error (not save error — that throws from saveLayout)
 *   refresh()  — re-fetch
 *   saveLayout(layout) — PUT the layout, throws ApiError on failure
 *   resetLayout()      — PUT { layout: null }, throws on failure
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api.js';

export function useCardLayout() {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLayout = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/dashboard/card-settings/layout');
      setLayout(data.layout);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError('Failed to load layout.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLayout();
  }, [fetchLayout]);

  const saveLayout = useCallback(async (nextLayout) => {
    const data = await apiFetch('/api/dashboard/card-settings/layout', {
      method: 'PUT',
      body: JSON.stringify({ layout: nextLayout }),
      headers: { 'Content-Type': 'application/json' },
    });
    setLayout(data.layout);
    return data;
  }, []);

  const resetLayout = useCallback(async () => {
    const data = await apiFetch('/api/dashboard/card-settings/layout', {
      method: 'PUT',
      body: JSON.stringify({ layout: null }),
      headers: { 'Content-Type': 'application/json' },
    });
    setLayout(null);
    return data;
  }, []);

  return { layout, loading, error, refresh: fetchLayout, saveLayout, resetLayout };
}
